import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckSquare, StickyNote, X, Inbox, ChevronRight } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import { formatRelativeTime } from '../lib/relativeTime';

interface QuickCapture {
  id: string;
  text: string;
  status: string;
  triaged_to_task_id: string | null;
  triaged_to_note_id: string | null;
  created_at: string;
}

interface Scope {
  id: string;
  name: string;
  color: string;
}

interface TriageTaskResponse {
  capture: QuickCapture;
  task: { id: string; title: string };
}

interface TriageNoteResponse {
  capture: QuickCapture;
  note: { id: string; title: string };
}

interface InboxTrayProps {
  onClose: () => void;
  onCountChange: () => void;
}

export function InboxTray({ onClose, onCountChange }: InboxTrayProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const [captures, setCaptures] = useState<QuickCapture[]>([]);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [captureScopes, setCaptureScopes] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [triagingIds, setTriagingIds] = useState<Set<string>>(new Set());
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [triagedItems, setTriagedItems] = useState<Record<string, { type: 'task' | 'note'; id: string }>>({});

  const load = useCallback(async () => {
    try {
      const [caps, sc] = await Promise.all([
        apiGet<QuickCapture[]>('/quick-captures?status=inbox'),
        apiGet<Scope[]>('/scopes'),
      ]);
      setCaptures(caps.slice(0, 7));
      setScopes(sc);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Listen for new captures
  useEffect(() => {
    function onCapture() { load(); }
    window.addEventListener('quick-capture-added', onCapture);
    return () => window.removeEventListener('quick-capture-added', onCapture);
  }, [load]);

  function animateOut(captureId: string, notifyCount = true) {
    setRemovingIds(prev => new Set(prev).add(captureId));
    setTimeout(() => {
      setCaptures(prev => prev.filter(c => c.id !== captureId));
      setRemovingIds(prev => {
        const next = new Set(prev);
        next.delete(captureId);
        return next;
      });
      setTriagedItems(prev => {
        const next = { ...prev };
        delete next[captureId];
        return next;
      });
      if (notifyCount) onCountChange();
    }, 300);
  }

  async function handleCreateTask(capture: QuickCapture) {
    if (triagingIds.has(capture.id)) return;
    setTriagingIds(prev => new Set(prev).add(capture.id));
    try {
      const scopeId = captureScopes[capture.id];
      const result = await apiPost<TriageTaskResponse>(`/quick-captures/${capture.id}/triage-task`, {
        title: capture.text,
        scope_ids: scopeId ? [scopeId] : undefined,
      });
      toast.success('Task created', {
        label: 'Open task',
        onClick: () => navigate(`/tasks/${result.task.id}`, { state: { from: '/inbox', fromLabel: 'Inbox' } }),
      });
      setTriagedItems(prev => ({ ...prev, [capture.id]: { type: 'task', id: result.task.id } }));
      onCountChange();
      setTimeout(() => animateOut(capture.id, false), 3000);
    } catch {
      toast.error('Failed to create task');
    } finally {
      setTriagingIds(prev => {
        const next = new Set(prev);
        next.delete(capture.id);
        return next;
      });
    }
  }

  async function handleCreateNote(capture: QuickCapture) {
    if (triagingIds.has(capture.id)) return;
    setTriagingIds(prev => new Set(prev).add(capture.id));
    try {
      const scopeId = captureScopes[capture.id];
      const result = await apiPost<TriageNoteResponse>(`/quick-captures/${capture.id}/triage-note`, {
        title: capture.text,
        scope_ids: scopeId ? [scopeId] : undefined,
      });
      toast.success('Note created', {
        label: 'Open note',
        onClick: () => navigate(`/notes/${result.note.id}`, { state: { from: '/inbox', fromLabel: 'Inbox' } }),
      });
      setTriagedItems(prev => ({ ...prev, [capture.id]: { type: 'note', id: result.note.id } }));
      onCountChange();
      setTimeout(() => animateOut(capture.id, false), 3000);
    } catch {
      toast.error('Failed to create note');
    } finally {
      setTriagingIds(prev => {
        const next = new Set(prev);
        next.delete(capture.id);
        return next;
      });
    }
  }

  async function handleDismiss(capture: QuickCapture) {
    if (triagingIds.has(capture.id)) return;
    setTriagingIds(prev => new Set(prev).add(capture.id));
    try {
      await apiPost(`/quick-captures/${capture.id}/dismiss`, {});
      toast.success('Dismissed');
      animateOut(capture.id);
    } catch {
      toast.error('Failed to dismiss');
    } finally {
      setTriagingIds(prev => {
        const next = new Set(prev);
        next.delete(capture.id);
        return next;
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  return (
    <>
    {/* Mobile backdrop — catches taps to close */}
    <div className="fixed inset-0 z-40 sm:hidden" onClick={onClose} />
    <div
      className="fixed left-3 right-3 top-[calc(52px+env(safe-area-inset-top)+8px)] max-h-[calc(100dvh-80px)] sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 sm:max-h-[500px] bg-bg-surface border border-border rounded-lg shadow-xl z-50 overflow-hidden flex flex-col"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Inbox size={16} className="text-accent" />
          <span className="text-sm font-semibold text-text-primary">Inbox</span>
          {captures.length > 0 && (
            <span className="text-xs text-text-muted bg-bg-hover rounded-full px-1.5 py-0.5">
              {captures.length}
            </span>
          )}
        </div>
        <Link
          to="/inbox"
          onClick={onClose}
          className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover transition-colors"
        >
          View all <ChevronRight size={12} />
        </Link>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent border-t-transparent" />
          </div>
        ) : captures.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Inbox size={24} className="mx-auto mb-2 text-text-muted opacity-50" />
            <p className="text-sm text-text-muted">Inbox is empty</p>
          </div>
        ) : (
          <div>
            {captures.map(capture => (
              <div
                key={capture.id}
                className={`px-4 py-3 border-b border-border/50 last:border-b-0 transition-all duration-300 ${
                  removingIds.has(capture.id)
                    ? 'opacity-0 max-h-0 py-0 overflow-hidden'
                    : 'opacity-100 max-h-40'
                } ${triagingIds.has(capture.id) ? 'opacity-60' : ''} ${
                  triagedItems[capture.id] ? 'bg-success/5 border-l-2 border-l-success/50' : ''
                }`}
              >
                <p className={`text-sm line-clamp-2 ${triagedItems[capture.id] ? 'text-text-secondary' : 'text-text-primary'}`}>
                  {capture.text}
                </p>
                <div className="flex items-center justify-between gap-2 mt-1.5">
                  <span className="text-[10px] text-text-muted flex-shrink-0">
                    {formatRelativeTime(capture.created_at)}
                  </span>
                  {triagedItems[capture.id] ? (
                    <Link
                      to={triagedItems[capture.id].type === 'task'
                        ? `/tasks/${triagedItems[capture.id].id}`
                        : `/notes/${triagedItems[capture.id].id}`}
                      state={{ from: '/inbox', fromLabel: 'Inbox' }}
                      onClick={onClose}
                      className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                        triagedItems[capture.id].type === 'task'
                          ? 'text-accent hover:text-accent-hover'
                          : 'text-[#14b8a6] hover:text-[#2dd4bf]'
                      }`}
                    >
                      {triagedItems[capture.id].type === 'task'
                        ? <><CheckSquare size={13} /> Open task</>
                        : <><StickyNote size={13} /> Open note</>}
                    </Link>
                  ) : (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {scopes.length > 0 && (
                      <select
                        value={captureScopes[capture.id] || ''}
                        onChange={(e) => setCaptureScopes(prev => ({ ...prev, [capture.id]: e.target.value }))}
                        className="px-1.5 py-1 bg-bg-input border border-border rounded text-[11px] text-text-primary max-w-[90px]"
                        title="Assign project"
                      >
                        <option value="">Project</option>
                        {scopes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    )}
                    <button
                      onClick={() => handleCreateTask(capture)}
                      disabled={triagingIds.has(capture.id)}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-accent/10 text-accent hover:bg-accent/20 active:bg-accent/30 transition-colors disabled:opacity-50"
                      title="Create task"
                    >
                      <CheckSquare size={11} />
                      Task
                    </button>
                    <button
                      onClick={() => handleCreateNote(capture)}
                      disabled={triagingIds.has(capture.id)}
                      className="flex items-center gap-1 px-2 py-1 text-[11px] rounded bg-[#14b8a6]/10 text-[#14b8a6] hover:bg-[#14b8a6]/20 active:bg-[#14b8a6]/30 transition-colors disabled:opacity-50"
                      title="Create note"
                    >
                      <StickyNote size={11} />
                      Note
                    </button>
                    <button
                      onClick={() => handleDismiss(capture)}
                      disabled={triagingIds.has(capture.id)}
                      className="p-1 text-text-muted hover:text-warning active:text-warning transition-colors disabled:opacity-50"
                      title="Dismiss"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
